import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Alert,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SavingsIcon from "@mui/icons-material/Savings";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { useDashboardWithPresentation } from "../hooks/useDashboardWithPresentation";
import { useDashboardTabState } from "../hooks/useDashboardTabState";
import { DashboardTabChrome, DashboardKpiSkeleton } from "./dashboard/DashboardTabChrome";
import { useTheme, alpha } from "@mui/material/styles";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import { getDashboardMetricInsights } from "../api/client";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";

const BR = (x) =>
  (Number.isFinite(x) ? x : Number(x ?? 0)).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const money = (x) => (x == null || !Number.isFinite(Number(x)) ? "—" : `R$ ${BR(x)}`);

function KpiCard({ icon, color, title, value, subtitle, titleAction }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar sx={{ bgcolor: alpha(color, 0.15), color }}>{icon}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {title}
              </Typography>
              {titleAction}
            </Stack>
            <Typography variant="h6" fontWeight={700} noWrap>
              {value}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function FinanceiroAtendimento({
  start,
  end,
  ids_origens = [],
  ids_tipos = [],
  ids_subtipo1 = [],
  ids_subtipo2 = [],
  ids_subtipo3 = [],
  ids_status = [],
  ids_usuarios = [],
  ids_agentes = [],
  ids_usuarios_interacao = [],
  ids_agentes_interacao = [],
  tags = [],
  tipo_responsavel = "",
  date_field = "entrada",
  all_period = false,
  ids_produtos = [],
  ids_campaigns = [],
  ids_whatsapp_campaigns = [],
  ids_motivos = [],
  ids_potenciais = [],
  ops_preset = "",
  stale_days,
  presentationSnapshot = null,
  readOnly = false,
}) {
  const theme = useTheme();
  const { t } = useTranslation("chat");

  React.useEffect(() => {
    ensureNamespaceLoaded("settings");
  }, []);

  const q = useDashboardWithPresentation(
    "financeiro-atendimento",
    "summary",
    {
      ...(all_period ? { all_period: 1 } : { start, end }),
      ids_origens,
      ids_tipos,
      ids_subtipo1,
      ids_subtipo2,
      ids_subtipo3,
      ids_status,
      ids_usuarios,
      ids_agentes,
      ids_usuarios_interacao,
      ids_agentes_interacao,
      tags,
      tipo_responsavel,
      date_field: date_field === "atualizacao" ? "atualizacao" : undefined,
      ...(Array.isArray(ids_produtos) && ids_produtos.length ? { ids_produtos } : {}),
      ...(Array.isArray(ids_campaigns) && ids_campaigns.length ? { ids_campaigns } : {}),
      ...(Array.isArray(ids_whatsapp_campaigns) && ids_whatsapp_campaigns.length
        ? { ids_whatsapp_campaigns }
        : {}),
      ...(Array.isArray(ids_motivos) && ids_motivos.length ? { ids_motivos } : {}),
      ...(Array.isArray(ids_potenciais) && ids_potenciais.length ? { ids_potenciais } : {}),
      ...(ops_preset ? { ops_preset } : {}),
      ...(ops_preset === "stale" && stale_days ? { stale_days } : {}),
    },
    { staleTime: 900_000, enabled: true },
    presentationSnapshot
  );

  const tab = useDashboardTabState(q, {
    registerShare: !readOnly && !presentationSnapshot,
  });
  const loading = tab.showSkeletons;
  const data = tab.data || {};
  const cards = data.cards || {};
  const charts = data.charts || {};
  const operadores = Array.isArray(data.operadores?.rows)
    ? data.operadores.rows
    : Array.isArray(data.operadores)
      ? data.operadores
      : [];

  const serie = (charts.serieTemporal || []).map((r) => ({
    day: r.day,
    custo: Number(r.custoAtendimentos || 0),
  }));

  const byTipo = charts.byTipoResponsavel || {};
  const tipoBars = ["humanos", "chatbots", "agentes"]
    .map((k) => ({
      name: k === "humanos" ? "Humanos" : k === "chatbots" ? "Chatbots" : "Agentes IA",
      volume: Number(byTipo[k]?.total || 0),
      custo: Number(byTipo[k]?.custoEquivalenteHumano || 0),
    }))
    .filter((r) => r.volume > 0 || r.custo > 0);

  const byCargo = (charts.byCargo || []).map((c) => ({
    cargo: c.cargo,
    custo: Number(c.custoAtendimentos || 0),
    ocioso: Number(c.custoOcioso || 0),
  }));

  const [metricDialogOpen, setMetricDialogOpen] = React.useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = React.useState(null);
  const [metricDialogName, setMetricDialogName] = React.useState("");
  const [metricDialogLabel, setMetricDialogLabel] = React.useState("");
  const [metricDialogData, setMetricDialogData] = React.useState(null);
  const [metricDialogError, setMetricDialogError] = React.useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = React.useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = React.useState(null);
  const lastCfgRef = React.useRef(null);

  const metricInsightsMutation = useMutation(
    async (cfg) => {
      const payload = {
        metric: cfg.metric,
        metricName: cfg.metricName,
        metricDefinition: cfg.metricDefinition,
        metricLabel: cfg.metricLabel || cfg.metricName,
        metricValue: cfg.metricValue,
        metricSec: cfg.metricSec,
        metricHuman: cfg.metricHuman,
        period: { start, end },
        summaryContext: {
          custoTotalAtendimentos: cards.custoTotalAtendimentos,
          custoTotalOcioso: cards.custoTotalOcioso,
          custoTotalPessoalPeriodo: cards.custoTotalPessoalPeriodo,
          custoMedioPorAtendimento: cards.custoMedioPorAtendimento,
          economiaEstimadaAutomacao: cards.economiaEstimadaAutomacao,
          custoHoraMedioHumanos: cards.custoHoraMedioHumanos,
          ocupacaoMediaPct: cards.ocupacaoMediaPct,
          operadoresComCusto: cards.operadoresComCusto,
        },
        filters: {
          ids_origens,
          ids_tipos,
          ids_status,
          ids_usuarios,
          ids_agentes,
          tipo_responsavel,
          date_field,
        },
      };
      return getDashboardMetricInsights("financeiro-atendimento", payload);
    },
    {
      onSuccess: (res) => {
        setMetricDialogData(res || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    lastCfgRef.current = cfg;
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => {
    if (readOnly) return null;
    return (
      <Tooltip title={t("analytics.explainAi")}>
        <IconButton size="small" onClick={() => openAi(cfg)} aria-label={t("analytics.explainAiAria")}>
          <AutoAwesomeIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    );
  };

  const kpiDefs = [
    {
      icon: <AttachMoneyIcon />,
      color: theme.palette.primary.main,
      title: "Custo dos atendimentos",
      value: money(cards.custoTotalAtendimentos),
      ai: {
        metric: "custo_total_atendimentos",
        metricName: "Custo dos atendimentos",
        metricLabel: "Custo dos atendimentos humanos no período",
        metricDefinition:
          "Parte produtiva da folha do período: esforço estimado (minutos padrão × volume, limitado à capacidade paga). Não usa TMA de ciclo.",
        metricValue: cards.custoTotalAtendimentos,
        metricHuman: money(cards.custoTotalAtendimentos),
      },
    },
    {
      icon: <HourglassEmptyIcon />,
      color: theme.palette.warning.main,
      title: "Custo ocioso (estim.)",
      value: money(cards.custoTotalOcioso),
      subtitle: "Capacidade paga − esforço estimado por atendimento",
      ai: {
        metric: "custo_total_ocioso",
        metricName: "Custo ocioso (estim.)",
        metricLabel: "Custo ocioso estimado",
        metricDefinition:
          "Folha correspondente às horas de capacidade não consumidas pelo esforço estimado (minutos padrão × atendimentos, limitado à jornada/pró-rata). Sem timesheet; TMA de ciclo não entra.",
        metricValue: cards.custoTotalOcioso,
        metricHuman: money(cards.custoTotalOcioso),
      },
    },
    {
      icon: <GroupsIcon />,
      color: theme.palette.info.main,
      title: "Custo pessoal no período",
      value: money(cards.custoTotalPessoalPeriodo),
      subtitle: cards.operadoresComCusto
        ? `${cards.operadoresComCusto} operador(es) com custo`
        : "Cadastre salários nos cargos/usuários",
      ai: {
        metric: "custo_total_pessoal",
        metricName: "Custo pessoal no período",
        metricLabel: "Custo de pessoal",
        metricDefinition:
          "Custo total de pessoal no período (salário + encargos rateados pela jornada cadastrada).",
        metricValue: cards.custoTotalPessoalPeriodo,
        metricHuman: money(cards.custoTotalPessoalPeriodo),
      },
    },
    {
      icon: <TrendingUpIcon />,
      color: theme.palette.success.main,
      title: "Custo médio / atendimento",
      value: money(cards.custoMedioPorAtendimento),
      subtitle: "Parte produtiva da folha ÷ volume (não é preço pago)",
      ai: {
        metric: "custo_medio_atendimento",
        metricName: "Custo médio / atendimento",
        metricLabel: "Custo médio por atendimento",
        metricDefinition:
          "Parte produtiva da folha (esforço estimado) dividida pelo volume. O valor pago é fixo; este indicador mede eficiência operacional.",
        metricValue: cards.custoMedioPorAtendimento,
        metricHuman: money(cards.custoMedioPorAtendimento),
      },
    },
    {
      icon: <SavingsIcon />,
      color: theme.palette.secondary?.main || theme.palette.success.dark,
      title: "Economia automação",
      value: money(cards.economiaEstimadaAutomacao),
      subtitle: `Bots ${money(cards.economiaChatbots)} · Agentes ${money(cards.economiaAgentes)}`,
      ai: {
        metric: "economia_automacao",
        metricName: "Economia automação",
        metricLabel: "Economia estimada com automação",
        metricDefinition:
          "Volume automatizado × custo médio humano por atendimento (folha rateada). Não usa TMA × R$/hora.",
        metricValue: {
          total: cards.economiaEstimadaAutomacao,
          chatbots: cards.economiaChatbots,
          agentes: cards.economiaAgentes,
        },
        metricHuman: money(cards.economiaEstimadaAutomacao),
      },
    },
    {
      icon: <SmartToyIcon />,
      color: theme.palette.text.secondary,
      title: "Custo/hora médio humanos",
      value: money(cards.custoHoraMedioHumanos),
      ai: {
        metric: "custo_hora_medio_humanos",
        metricName: "Custo/hora médio humanos",
        metricLabel: "Custo por hora médio",
        metricDefinition:
          "Custo médio por hora dos operadores com salário configurado (salário + encargos ÷ horas mensais da jornada).",
        metricValue: cards.custoHoraMedioHumanos,
        metricHuman: money(cards.custoHoraMedioHumanos),
      },
    },
  ];

  return (
    <Box>
      <DashboardTabChrome
        isDeferred={tab.isDeferred}
        isPreview={tab.isPreview}
        isInitialLoading={tab.showSkeletons}
        errorMessage={tab.errorMessage}
      />

      {cards.note ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {cards.note}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <DashboardKpiSkeleton />
              </Grid>
            ))
          : kpiDefs.map((k) => (
              <Grid item xs={12} sm={6} md={4} key={k.title}>
                <KpiCard
                  icon={k.icon}
                  color={k.color}
                  title={k.title}
                  value={k.value}
                  subtitle={k.subtitle}
                  titleAction={<AiBtn cfg={k.ai} />}
                />
              </Grid>
            ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                  Custo diário dos atendimentos humanos
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "serie_custo_diario",
                    metricName: "Custo diário dos atendimentos humanos",
                    metricDefinition:
                      "Série diária do custo estimado dos atendimentos humanos no período, para identificar picos de custo e sazonalidade operacional.",
                    metricValue: serie,
                    metricHuman: "",
                  }}
                />
              </Stack>
              <Box sx={{ height: 280 }}>
                {loading ? (
                  <DashboardKpiSkeleton />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={serie}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RTooltip formatter={(v) => money(v)} />
                      <Line
                        type="monotone"
                        dataKey="custo"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                  Humanos vs Chatbots vs Agentes
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "custo_por_tipo_responsavel",
                    metricName: "Humanos vs Chatbots vs Agentes",
                    metricDefinition:
                      "Comparativo de volume e custo equivalente entre atendimentos humanos, chatbots e agentes de IA no período.",
                    metricValue: tipoBars,
                    metricHuman: "",
                  }}
                />
              </Stack>
              <Box sx={{ height: 280 }}>
                {loading ? (
                  <DashboardKpiSkeleton />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={tipoBars}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RTooltip formatter={(v, name) => (name === "custo" ? money(v) : v)} />
                      <Bar dataKey="volume" fill={theme.palette.info.main} name="Volume" />
                      <Bar dataKey="custo" fill={theme.palette.success.main} name="Custo equiv." />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {byCargo.length ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                Custo por cargo
              </Typography>
              <AiBtn
                cfg={{
                  metric: "custo_por_cargo",
                  metricName: "Custo por cargo",
                  metricDefinition:
                    "Distribuição do custo de atendimentos e ociosidade por cargo, para priorizar dimensionamento e treinamento.",
                  metricValue: byCargo,
                  metricHuman: "",
                }}
              />
            </Stack>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCargo} layout="vertical" margin={{ left: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="cargo" width={80} tick={{ fontSize: 11 }} />
                  <RTooltip formatter={(v) => money(v)} />
                  <Bar dataKey="custo" fill={theme.palette.primary.main} name="Atendimentos" />
                  <Bar dataKey="ocioso" fill={theme.palette.warning.main} name="Ocioso" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              Operadores — custo e ociosidade
            </Typography>
            <AiBtn
              cfg={{
                metric: "operadores_custo_ociosidade",
                metricName: "Operadores — custo e ociosidade",
                metricDefinition:
                  "Detalhamento por operador: volume, custo da parte produtiva (esforço estimado) e ociosidade (capacidade − esforço).",
                metricValue: operadores.slice(0, 25).map((r) => ({
                  operador: r.operador,
                  cargo: r.cargoNome,
                  atendimentos: r.total_atendimentos,
                  custoAtendimentos: r.custo_atendimentos,
                  custoOcioso: r.custo_ocioso,
                  custoHora: r.custo_hora,
                })),
                metricHuman: `${operadores.length} operador(es)`,
              }}
            />
          </Stack>
          <Box sx={{ height: 420 }}>
            <DataGrid
              density="compact"
              rows={operadores.map((r) => ({ id: r.idUsuario, ...r }))}
              columns={[
                { field: "operador", headerName: "Operador", flex: 1.2, minWidth: 140 },
                { field: "cargoNome", headerName: "Cargo", flex: 1, minWidth: 120 },
                { field: "modalidade", headerName: "Mod.", width: 90 },
                {
                  field: "total_atendimentos",
                  headerName: "Atend.",
                  width: 90,
                  type: "number",
                },
                {
                  field: "custo_medio_por_atendimento",
                  headerName: "R$/atend.",
                  width: 110,
                  valueFormatter: (value) => money(value),
                },
                {
                  field: "custo_atendimentos",
                  headerName: "Custo atend.",
                  width: 120,
                  valueFormatter: (value) => money(value),
                },
                {
                  field: "custo_ocioso",
                  headerName: "Custo ocioso",
                  width: 120,
                  valueFormatter: (value) => money(value),
                },
                {
                  field: "custo_hora",
                  headerName: "R$/hora",
                  width: 100,
                  valueFormatter: (value) => money(value),
                },
              ]}
              loading={loading}
              disableSelectionOnClick
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        </CardContent>
      </Card>

      {!readOnly ? (
        <MetricAiInsightsDialog
          open={metricDialogOpen}
          metric={metricDialogMetric}
          metricName={metricDialogName}
          metricLabel={metricDialogLabel}
          metricHuman={metricDialogHuman}
          period={{ start, end }}
          loading={metricInsightsMutation.isLoading}
          error={metricDialogError}
          data={metricDialogData}
          scope="financeiro-atendimento"
          basePayload={metricDialogBasePayload}
          onClose={() => setMetricDialogOpen(false)}
        />
      ) : null}
    </Box>
  );
}
